import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function AuditReadinessPage() {
  const faqs = [
    {
      question: 'What is audit readiness?',
      answer: 'Audit readiness refers to an organization\'s ability to demonstrate, at any point in time, how software changes are controlled and reviewed. It includes defined procedures and evidence that these procedures are followed consistently.'
    },
    {
      question: 'What evidence do auditors typically request?',
      answer: 'Auditors typically request evidence for sampled changes including: links to work items, pull requests with descriptions, peer approval records, CI/CD check results, deployment timestamps, and post-deployment verification.'
    },
    {
      question: 'Why is continuous evidence better than point-in-time collection?',
      answer: 'Continuous evidence is captured contemporaneously as events occur, demonstrating consistent control operation throughout the audit period. Point-in-time collection is retroactive, time-consuming, and cannot prove controls operated all year.'
    },
    {
      question: 'How much time does manual evidence collection take?',
      answer: 'Collecting evidence for 25 sampled changes typically takes 8-16 hours of engineering time per audit when done manually with screenshots and exports.'
    },
    {
      question: 'What are common audit findings for change control?',
      answer: 'Common findings include missing approval evidence for emergency changes, incomplete linkage between tickets and code, optional (not enforced) CI checks, undocumented branch protection changes, and evidence collected after-the-fact instead of contemporaneously.'
    }
  ];

  return (
    <MarketingLayout
      title="Audit Readiness for Software Change Control"
      description="Understand audit readiness needs for software change control in SOC 2 and ISO 27001 programs. Evidence preparation and continuous readiness."
      h1="Audit Readiness for Software Change Control"
      heroText="Audit readiness refers to an organization's ability to demonstrate, at any point in time, how software changes are controlled and reviewed."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Audit Readiness', url: 'https://evidenttrail.com/audit-readiness' }
        ]}
      />
      <FaqJsonLd items={faqs} />

      {/* What Auditors Expect */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What SOC 2 and ISO 27001 Readiness Reviews Typically Cover</h2>
            <p className={`${tokens.text.base} mb-6`}>
              For SOC 2 and ISO 27001 readiness reviews, this typically includes:
            </p>
            <div className={tokens.card}>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>Defined change management procedures</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>Evidence that procedures are followed in practice</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>The ability to trace individual changes to approvals and reviews</span>
                </li>
              </ul>
            </div>
            <p className={`${tokens.text.base} mt-6`}>
              Audit readiness is not limited to documentation. Auditors usually verify readiness by sampling real changes from production systems and validating the associated evidence.
            </p>
          </div>
        </div>
      </section>

      {/* Evidence Preparation */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How Evidence is Prepared and Reviewed</h2>
            <p className={`${tokens.text.base} mb-6`}>
              During audit fieldwork, organizations typically receive a sample of changes selected from production deployments over the audit period. For each sampled change, you must provide:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Required Evidence Per Change</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>• Link to approved work item (Jira/ADO ticket)</li>
                  <li>• Pull request with description and reviewers</li>
                  <li>• Evidence of peer approval (GitHub approval review)</li>
                  <li>• CI/CD check results (pass/fail status)</li>
                  <li>• Deployment timestamp and target environment</li>
                  <li>• Post-deployment verification (if applicable)</li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Common Audit Findings</h3>
                <ul className="space-y-2 text-sm text-amber-200">
                  <li>⚠️ Missing approval evidence for emergency changes</li>
                  <li>⚠️ Incomplete linkage between tickets and code</li>
                  <li>⚠️ CI checks were optional, not enforced</li>
                  <li>⚠️ Branch protection disabled temporarily (no record)</li>
                  <li>⚠️ Evidence collected after-the-fact (not contemporaneous)</li>
                </ul>
              </div>
            </div>

            <h3 className={`${tokens.heading.h3} mb-4 mt-8`}>The Manual Evidence Challenge</h3>
            <p className={`${tokens.text.base} mb-4`}>
              Most organizations scramble during audit season to manually assemble screenshots and export reports. This approach has several problems:
            </p>
            <div className={`${tokens.card} mb-6`}>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Time-consuming:</strong> Collecting evidence for 25 sampled changes can take 8-16 hours of engineering time per audit.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Error-prone:</strong> Screenshots may not capture all required fields, or links may be broken months later.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Not contemporaneous:</strong> Auditors prefer evidence captured at the time of the change, not assembled retroactively.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Limited coverage:</strong> Sampling only covers 5-10% of changes, leaving the other 90% unverified.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How EvidentTrail Fits */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail Supports Audit Readiness</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail maintains a continuous record of software changes, approvals, and CI checks from GitHub using <Link to="/read-only-github-audit" className="text-primary-400 hover:underline">read-only GitHub audit access</Link>. This evidence is available for audit sampling at any time, without manual collection. For traceability, we also link changes to approved <Link to="/work-item-linking" className="text-primary-400 hover:underline">work items</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/github-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">GitHub Change Control</h4>
                <p className="text-sm text-slate-400">Turn GitHub pull requests into formal change control records.</p>
              </Link>
              <Link to="/work-item-linking" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Work Item Linking</h4>
                <p className="text-sm text-slate-400">Connect pull requests to approved work items for traceability.</p>
              </Link>
              <Link to="/tools" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Evidence Tools</h4>
                <p className="text-sm text-slate-400">Generate a one-off evidence pack.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Learn More About Audit Evidence Collection</h2>
            <p className={`${tokens.text.base} mb-8`}>
              See how continuous evidence collection supports audit readiness.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/faq" className="text-slate-300 hover:text-white transition-colors">
                Security and audit FAQ →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
